import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getVoicePreviewClient } from '@/lib/api-clients';

export interface VoicePreview {
  /** Play the line in the agent's voice; pressing again while it plays stops it. */
  toggle: () => void;
  /** True while the line is being synthesized. */
  loading: boolean;
  /** True while it is playing. */
  playing: boolean;
}

/** The preview playing anywhere on the page, so starting one stops the other. */
let current: { stop: () => void } | null = null;
/** Bumped on every press, so only the line pressed LAST plays when its audio arrives. */
let latestPress = 0;

/**
 * Synthesized lines, page-wide, keyed by text: the detail row and the editor
 * show the same line, and playing it from both should ask the API once. Oldest
 * first; a few entries, since a setup screen auditions a handful of lines.
 */
const CACHE_SIZE = 8;
const cache = new Map<string, string>();

function cachedUrl(text: string): string | undefined {
  const url = cache.get(text);
  if (url !== undefined) {
    // Most recently used goes to the back, so eviction takes the stalest.
    cache.delete(text);
    cache.set(text, url);
  }
  return url;
}

function remember(text: string, blob: Blob): string {
  const url = URL.createObjectURL(blob);
  cache.set(text, url);
  for (const [oldText, oldUrl] of cache) {
    if (cache.size <= CACHE_SIZE) break;
    cache.delete(oldText);
    URL.revokeObjectURL(oldUrl);
  }
  return url;
}

/** Forget every cached line and who is playing. For tests. */
export function resetVoicePreview(): void {
  for (const url of cache.values()) URL.revokeObjectURL(url);
  cache.clear();
  current = null;
  latestPress = 0;
}

/**
 * An empty WAV. Played inside the click to unlock the element: Safari lets an
 * element play after an await only if `play()` was called on it during the
 * user's gesture, and the synthesized line arrives a second or two later.
 */
const SILENCE =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/**
 * Hear `text` the way the voice agent says it (ADR-344). The API synthesizes it
 * in the agent's voice.
 *
 * Errors are toasted HERE, not by the global mutation handler (which this
 * mutation opts out of): a request this hook abandoned — the line changed, the
 * screen closed — is not an error, and only this hook knows which those are.
 *
 * When `text` changes, a line still playing or still being fetched for the old
 * text is stopped: the button now stands beside a different line.
 */
export function useVoicePreview(text: string): VoicePreview {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  const stop = useCallback((): void => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.pause();
      // Only this hook's audio is ever in `current` while `audioRef` holds it.
      current = null;
    }
    setPlaying(false);
  }, []);

  const play = useCallback(
    (audio: HTMLAudioElement, url: string): void => {
      current?.stop();
      current = { stop };
      audioRef.current = audio;
      audio.src = url;
      audio.onended = stop;
      setPlaying(true);
      audio.play().catch((err: unknown) => {
        stop();
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          toast.error('The browser blocked the audio. Press play again.');
        }
      });
    },
    [stop]
  );

  const { mutate, isPending } = useMutation({
    mutationFn: ({ line, signal }: { line: string; signal: AbortSignal }) =>
      getVoicePreviewClient().speak({ text: line }, signal),
    meta: { suppressGlobalError: true },
  });

  const toggle = useCallback((): void => {
    if (audioRef.current) {
      stop();
      return;
    }
    const press = ++latestPress;
    const audio = new Audio(SILENCE);
    void audio.play().catch(() => {});

    const url = cachedUrl(text);
    if (url !== undefined) {
      play(audio, url);
      return;
    }
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    mutate(
      { line: text, signal: request.signal },
      {
        // Per-call callbacks: dropped if the button has unmounted, so a line
        // that arrives after its screen closed is never played.
        onSuccess: (blob) => {
          const fetched = remember(text, blob);
          if (!request.signal.aborted && press === latestPress) play(audio, fetched);
        },
        onError: (err) => {
          if (!request.signal.aborted) {
            toast.error(err instanceof Error ? err.message : 'The voice preview failed');
          }
        },
      }
    );
  }, [text, mutate, play, stop]);

  // A new line, or leaving the screen: stop what is playing and drop a request
  // still out. Cached audio stays — another button may play the same line.
  useEffect(
    () => () => {
      requestRef.current?.abort();
      requestRef.current = null;
      stop();
    },
    [text, stop]
  );

  // An abandoned request rejects as soon as it is aborted, so `isPending` clears with it.
  return { toggle, loading: isPending, playing };
}
